'use client';

import * as React from 'react';
import { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { ErrorPanel, JucyMuiTabPanel, JucyMuiTabs } from '@jucy-ui/components';
import { JucyMuiAccordion, JucyMuiAccordionDetails, JucyMuiAccordionSummary } from '@jucy-ui/components/JucyMuiAccordion';
import { BlockContainer } from '@/components/Blocks/BlockContainer';
import { CmsBlock } from '@/components/Blocks/CmsBlock';
import { BlockTitle } from '@/components/Blocks/components/BlockTitle';
import { BoldTab } from '@/components/BoldTab';
import { slugify } from '@/lib/slugify';
import { CmsBlock as CmsBlockData } from '@/services/cms/blocks';
import { CmsBaseBlock } from '@/services/cms/blocks/BaseCmsBlock';
import { CmsTabBlock } from '@/services/cms/blocks/CmsTabBlock';

export type TabBlockProps = CmsTabBlock & {
    sx?: SxProps<Theme>;
    children?: React.ReactNode;
};

export const TabBlock = ({ sx, children, ...props }: TabBlockProps) => {
    const tabs = (props.elements?.elements || []) as CmsBaseBlock[];
    const [activeTab, setActiveTab] = useState<number | false>(tabs[0]?.id || 0);
    const toggle = useCallback((_event: React.SyntheticEvent, newValue: number | false) => setActiveTab(newValue), []);
    if (!tabs.length) {
        return (
            <BlockContainer sx={sx} {...props}>
                <BlockTitle {...props} />
                <ErrorPanel title="No tabs found" />
            </BlockContainer>
        );
    }
    return (
        <BlockContainer sx={sx} {...props}>
            <BlockTitle {...props} />
            <Box
                sx={{
                    display: { xs: props.mobileAccordion ? 'none' : 'block', sm: 'block' },
                }}
            >
                <JucyMuiTabs variant="fullWidth" value={activeTab} onChange={toggle}>
                    {tabs.map((block: CmsBaseBlock) => (
                        <BoldTab key={block.id} value={block.id} label={block.title} a11yName={slugify(`${block.title}`)} />
                    ))}
                </JucyMuiTabs>
                {tabs.map((block: CmsBaseBlock) => (
                    <JucyMuiTabPanel sx={{ pt: 2 }} key={block.id} activeValue={activeTab} value={block.id} a11yName={slugify(`${block.title}`)}>
                        <CmsBlock position={1} isSubBlock={true} key={`cms-block-${block.id}`} data={block as CmsBlockData} />
                    </JucyMuiTabPanel>
                ))}
            </Box>
            {props.mobileAccordion ? (
                <Box
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                    }}
                >
                    {tabs.map((block) => {
                        const active = block.id === activeTab;
                        return (
                            <JucyMuiAccordion
                                sx={{ background: 'none' }}
                                key={block.id}
                                expanded={active}
                                onChange={(e) => {
                                    if (activeTab === block.id) {
                                        toggle(e, false);
                                    } else {
                                        toggle(e, block.id);
                                    }
                                }}
                            >
                                <JucyMuiAccordionSummary
                                    sx={(theme) => ({
                                        mb: 2,
                                        ...(active
                                            ? {
                                                  borderBottom: `4px solid ${theme.palette.primary.main}`,
                                              }
                                            : {
                                                  borderBottom: `1px solid ${theme.palette.background.light}`,
                                                  color: theme.palette.charcoal.light,
                                              }),
                                    })}
                                    variant="chevron"
                                >
                                    <Typography variant="subtitle1" sx={{ textTransform: 'uppercase' }}>
                                        {block.title}
                                    </Typography>
                                </JucyMuiAccordionSummary>
                                <JucyMuiAccordionDetails sx={(theme) => ({ p: { xs: theme.spacing(0, 2, 1, 2) } })}>
                                    <CmsBlock position={1} isSubBlock={true} key={`cms-block-${block.id}`} data={block as CmsBlockData} />
                                </JucyMuiAccordionDetails>
                            </JucyMuiAccordion>
                        );
                    })}
                </Box>
            ) : null}
        </BlockContainer>
    );
};
